import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Phone } from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import SectorContactForm from './SectorContactForm';
import { getSectorContacts, saveSectorContacts, deleteSectorContacts } from '../utils/api';

const SectorContactList = () => {
  const [phoneNumbers, setPhoneNumbers] = useState({});
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [currentSector, setCurrentSector] = useState(null);

  // Fetch phone numbers from API
  const fetchPhoneNumbers = async () => {
    setLoading(true);
    try {
      const data = await getSectorContacts();
      setPhoneNumbers(data);
    } catch (error) {
      toast.error('Failed to fetch phone numbers', {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPhoneNumbers();
  }, []);

  const handleSave = async (sector, phoneNumbers) => {
    try {
      await saveSectorContacts(sector, phoneNumbers);
      toast.success('Phone numbers saved successfully', {
        position: "top-right",
        autoClose: 3000,
      });
      fetchPhoneNumbers();
      setModalVisible(false);
    } catch (error) {
      toast.error('Failed to save phone numbers', {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  const handleDelete = async (sector) => {
    if (window.confirm('Are you sure you want to delete these phone numbers?')) {
      try {
        await deleteSectorContacts(sector);
        toast.success('Phone numbers deleted successfully', {
          position: "top-right",
          autoClose: 3000,
        });
        fetchPhoneNumbers();
      } catch (error) {
        toast.error('Failed to delete phone numbers', {
          position: "top-right",
          autoClose: 3000,
        });
      }
    }
  };

  const sectors = Object.keys(phoneNumbers).sort();

  return (
    <div className="container mx-auto p-4">
      <ToastContainer />
      
      <div className="mb-6">
        <button
          onClick={() => {
            setCurrentSector(null);
            setModalVisible(true);
          }}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          <Plus size={18} />
          Add Sector Phone Numbers
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sectors.length > 0 ? (
            sectors.map(sector => (
              <div key={sector} className="border rounded-lg p-4 shadow-sm">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">{sector}</h3>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setCurrentSector(sector);
                        setModalVisible(true);
                      }}
                      className="text-blue-600 hover:text-blue-800 p-1"
                      title="Edit"
                    >
                      <Edit2 size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(sector)}
                      className="text-red-600 hover:text-red-800 p-1"
                      title="Delete"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  {phoneNumbers[sector]?.map((phone, index) => (
                    <div key={index} className="flex items-center gap-2 p-2 border-b last:border-0">
                      <Phone size={16} className="text-gray-500" />
                      <span>{phone}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full text-center py-8 text-gray-500">
              <Phone size={48} className="mx-auto mb-4 text-gray-300" />
              <p>No sector phone numbers found. Add your first sector to get started.</p>
            </div>
          )}
        </div>
      )}

      {modalVisible && (
        <SectorContactForm
          sector={currentSector}
          initialPhoneNumbers={currentSector ? phoneNumbers[currentSector] : []}
          onSave={handleSave}
          onClose={() => setModalVisible(false)}
        />
      )}
    </div>
  );
};

export default SectorContactList;